/* eslint-disable react-refresh/only-export-components */
/**
 * ErrorBoundary — powered by react-error-boundary (bvaughn, 7k+ ★)
 * https://github.com/bvaughn/react-error-boundary
 *
 * Catches render-phase errors, displays a branded fallback, and lets the
 * user retry without a full page reload.  Wraps the entire app in App.tsx
 * and can also be used per-route for granular recovery.
 */
import { cn } from "@/lib/utils";
import { AlertTriangle, Home, RotateCcw } from "lucide-react";
import { type ReactNode, useCallback } from "react";
import {
  ErrorBoundary as ReactErrorBoundary,
  type FallbackProps,
} from "react-error-boundary";
import { toast } from "sonner";

// ── Fallback UI ─────────────────────────────────────────────────────────────
function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const err = error instanceof Error ? error : new Error(String(error));
  return (
    <div className="flex items-center justify-center min-h-screen p-8 bg-background text-foreground">
      <div className="flex flex-col items-center w-full max-w-2xl p-8">
        <AlertTriangle
          size={48}
          className="text-destructive mb-6 flex-shrink-0 animate-pulse"
        />

        <h2 className="text-xl font-semibold mb-2">
          Something went wrong
        </h2>
        <p className="text-sm text-muted-foreground mb-6 text-center max-w-md">
          An unexpected error occurred. You can try recovering below, or
          reload the page if the problem persists.
        </p>

        <details className="w-full mb-6">
          <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground transition-colors">
            Technical details
          </summary>
          <div className="mt-2 p-4 w-full rounded bg-muted overflow-auto">
            <pre className="text-xs text-muted-foreground whitespace-break-spaces">
              {err.message}
              {"\n\n"}
              {err.stack}
            </pre>
          </div>
        </details>

        <div className="flex gap-3">
          <button
            onClick={resetErrorBoundary}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg",
              "bg-primary text-primary-foreground",
              "hover:opacity-90 cursor-pointer",
              "transition-all duration-150 active:scale-95"
            )}
          >
            <RotateCcw size={16} />
            Try Again
          </button>

          <button
            onClick={() => {
              window.location.href = "/";
            }}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg",
              "bg-muted text-muted-foreground",
              "hover:bg-accent hover:text-accent-foreground cursor-pointer",
              "transition-all duration-150 active:scale-95"
            )}
          >
            <Home size={16} />
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Error logger ────────────────────────────────────────────────────────────
function logError(error: unknown, info: { componentStack?: string | null }) {
  console.error("[ErrorBoundary]", error);
  if (info.componentStack) {
    console.error("[ErrorBoundary] Component stack:", info.componentStack);
  }
  const msg = error instanceof Error ? error.message : String(error);
  toast.error("An error occurred", {
    description: msg?.slice(0, 120),
    duration: 5000,
  });
}

// ── Exported wrapper ────────────────────────────────────────────────────────
interface Props {
  children: ReactNode;
  /** Optional key that forces a boundary reset when it changes (e.g. route path) */
  resetKey?: string;
}

export default function ErrorBoundary({ children, resetKey }: Props) {
  const handleReset = useCallback(() => {
    // Clear any stale query cache on reset to avoid re-triggering the same error
    // (the QueryClient is global, so this is safe)
    window.dispatchEvent(new Event("error-boundary-reset"));
  }, []);

  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={logError}
      onReset={handleReset}
      resetKeys={resetKey ? [resetKey] : undefined}
    >
      {children}
    </ReactErrorBoundary>
  );
}

// Re-export for granular per-component usage
export { ReactErrorBoundary, ErrorFallback, logError };
